'use client'

import { Button } from '@/components/ui/button'
import { useI18n } from '@/lib/i18n'
import { Activity, CloudDownload, Cpu, Database, DollarSign, Globe, Mail, Menu, Moon, RefreshCw, Search, ShieldBan, ShieldCheck, Sun, X } from 'lucide-react'
import { useTheme } from 'next-themes'
import { useEffect, useState } from 'react'

// Browser globals are configured in ESLint config

const navItems = [
  { id: 'model-catalog', key: 'Model Catalog', icon: CloudDownload },
  { id: 'game-model-pool', key: 'admin.nav.gameModelPool', icon: Cpu },
  { id: 'statics', key: 'admin.nav.statics', icon: Activity },
  { id: 'stimulation', key: 'admin.nav.stimulation', icon: Activity },
  { id: 'email-service', key: 'Email Service', icon: Mail },
  { id: 'query', key: 'admin.nav.query', icon: Search },
  { id: 'block-list', key: 'admin.nav.blockList', icon: ShieldBan },
  { id: 'whitelist', key: 'admin.nav.whitelist', icon: ShieldCheck },
  { id: 'token-cost', key: 'Token Cost', icon: DollarSign },
  { id: 'reconcile', key: 'Reconcile', icon: RefreshCw },
  { id: 'cache', key: 'Cache', icon: Database },
]

const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'zh', name: '简体中文', flag: '🇨🇳' },
]

export function AdminNav() {
  const [activeSection, setActiveSection] = useState('stimulation')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false)
  const { theme, setTheme } = useTheme()
  const [mounted, setMounted] = useState(false)
  const { language, setLanguage, t } = useI18n()

  useEffect(() => {
    setMounted(true)
  }, [])

  // Handle scroll to update active section
  useEffect(() => {
    if (typeof window === 'undefined')
      return

    const handleScroll = () => {
      const sections = navItems.map(item => document.getElementById(item.id))
      const scrollPosition = window.scrollY + 100

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i]
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(navItems[i]!.id)
          break
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    if (typeof window === 'undefined')
      return

    const handleClickOutside = (event: Event) => {
      if (showLanguageDropdown) {
        const target = event.target as HTMLElement
        if (!target.closest('.language-dropdown')) {
          setShowLanguageDropdown(false)
        }
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [showLanguageDropdown])

  const handleLanguageChange = (languageCode: string) => {
    console.log(`Language changing to: ${languageCode}`)
    setLanguage(languageCode)
    setShowLanguageDropdown(false)
  }

  const scrollToSection = (sectionId: string) => {
    if (typeof window === 'undefined')
      return
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
      setMobileMenuOpen(false)
    }
  }

  const currentLang
    = languages.find(lang => lang.code === language) || languages[0]!

  if (!mounted) {
    return null
  }

  return (
    <nav className="fixed inset-x-0 top-0 z-50 border-b border-border bg-background/95 backdrop-blur-md">
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <div className="flex items-center">
            <h1 className="text-xl font-bold text-foreground">
              {t('admin.brand')}
            </h1>
          </div>

          <div className="flex flex-shrink-0 items-center gap-2">
            {/* Language Dropdown */}
            <div className="language-dropdown relative">
              <Button
                variant="ghost"
                size="icon"
                className="relative"
                title={`Current language: ${currentLang.name}`}
                onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
              >
                <Globe className="size-4" />
                <span className="sr-only">
                  Select language - Current: {currentLang.name}
                </span>
              </Button>

              {showLanguageDropdown && (
                <div className="absolute right-0 top-full z-50 mt-2 w-40 rounded-md border border-border bg-popover shadow-md">
                  {languages.map(language => (
                    <button
                      key={language.code}
                      onClick={() => handleLanguageChange(language.code)}
                      className={`flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-muted ${
                        currentLang?.code === language.code ? 'bg-muted' : ''
                      }`}
                    >
                      <span className="text-base">{language.flag}</span>
                      <span>{language.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            >
              <Sun className="size-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
              <Moon className="absolute size-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
              <span className="sr-only">Toggle theme</span>
            </Button>

            {/* Hamburger Menu Button (all screen sizes) */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="size-4" />
              ) : (
                <Menu className="size-4" />
              )}
              <span className="sr-only">Toggle menu</span>
            </Button>
          </div>
        </div>

        {/* Hamburger dropdown menu (all screen sizes) */}
        {mobileMenuOpen && (
          <div className="absolute right-4 top-16 z-50 max-h-[75vh] w-64 overflow-y-auto rounded-md border border-border bg-popover shadow-lg">
            <div className="space-y-1 p-2">
              {navItems.map((item) => {
                const IconComponent = item.icon
                return (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium transition-colors ${
                      activeSection === item.id
                        ? 'bg-muted text-foreground'
                        : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                    }`}
                  >
                    <IconComponent className="size-4" />
                    {t(item.key)}
                  </button>
                )
              })}

              {/* Mobile Language Selection */}
              <div className="mt-2 border-t border-border pt-2">
                <div className="px-3 py-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Language
                </div>
                {languages.map(language => (
                  <button
                    key={language.code}
                    onClick={() => handleLanguageChange(language.code)}
                    className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors ${
                      currentLang?.code === language.code
                        ? 'bg-muted text-foreground'
                        : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                    }`}
                  >
                    <span className="text-base">{language.flag}</span>
                    <span>{language.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  )
}
